import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatShopMoney } from '@theme/money-formatting';
import {
    getCartLineSubtotalCents,
    resolveDiscountProgress,
    resolveDiscountSegmentProgress,
} from '@theme/na-discount-progress-state';

/**
 * @typedef {{
 *   message: HTMLElement,
 *   steps: HTMLElement[],
 *   iconTemplates: HTMLElement
 * }} DiscountProgressBarRefs
 */

/**
 * @typedef {{
 *   qualifyingTags: string[],
 *   types: string[],
 *   allItems: boolean,
 *   minItems: number,
 *   labels: { tier1: string, tier2: string, tier3: string },
 *   messages: { m1: string, m2: string, m3: string, final: string },
 *   tiers: { thresholdCents: number, discountCents: number }[],
 *   colors: {
 *     active: string,
 *     inactive: string,
 *     lineIncomplete: string,
 *     tagBg: string,
 *     tagText: string,
 *     message: string
 *   }
 * }} DiscountProgressBarConfig
 */

/** @typedef {{ items?: Record<string, unknown>[] }} CartResource */

/** @extends {Component<DiscountProgressBarRefs>} */
class NaDiscountProgressBarComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['message', 'iconTemplates'];

    /** @type {Map<string, string[]>} */
    static #tagCache = new Map();

    static #CELEBRATE_MS = 720;
    static #ALLOWED_TAGS = new Set(['p', 'strong', 'em', 'b', 'i', 'br', 'span', 'a']);

    /** @type {Map<string, number>} Static so it survives re-renders that swap in a bar already at the new progress. */
    static #lastLineScale = new Map();

    /** @type {number} */
    #previousTier = -1;

    /** @type {AbortController | null} */
    #abortController = null;

    /** @type {IntersectionObserver | null} */
    #lineObserver = null;

    /** @type {Animation[]} */
    #lineAnimations = [];

    connectedCallback() {
        super.connectedCallback();
        this.#abortController = new AbortController();
        document.addEventListener(ThemeEvents.cartUpdate, this.update, { signal: this.#abortController.signal });
        this.#animateLinesFromLastScale();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController?.abort();
        this.#lineObserver?.disconnect();
    }

    updatedCallback() {
        this.#applyThemeVars(this.#readConfig().colors);
        this.#animateLinesFromLastScale();
    }

    #animateLinesFromLastScale() {
        /** @type {{ key: string, line: HTMLElement, from: number, to: number }[]} */
        const pending = [];

        this.querySelectorAll('[data-step]').forEach((step) => {
            const line = step.querySelector('[data-dpb-line]');
            if (!(line instanceof HTMLElement)) return;

            const key = step.getAttribute('data-step') ?? '';
            const to =
                step.getAttribute('data-state') === 'completed'
                    ? 1
                    : Math.min(Math.max(Number(line.style.getPropertyValue('--dpb-progress')) || 0, 0), 1);
            const from = NaDiscountProgressBarComponent.#lastLineScale.get(key) ?? 0;
            if (Math.abs(from - to) >= 0.001) pending.push({ key, line, from, to });
        });

        this.#lineObserver?.disconnect();
        this.#lineAnimations.forEach((animation) => animation.cancel());
        this.#lineAnimations = [];
        if (!pending.length) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            pending.forEach(({ key, to }) => NaDiscountProgressBarComponent.#lastLineScale.set(key, to));
            return;
        }

        this.#lineAnimations = pending.map(({ line, from, to }, index) => {
            const animation = line.animate([{ transform: `scaleX(${from})` }, { transform: `scaleX(${to})` }], {
                duration: 500,
                delay: index * 80,
                easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
                fill: 'backwards',
                pseudoElement: '::after',
            });
            animation.pause();
            return animation;
        });

        // The cart drawer is usually closed when the cart changes, so hold the old fill until the bar is on screen.
        this.#lineObserver = new IntersectionObserver((entries, observer) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            pending.forEach(({ key, to }) => NaDiscountProgressBarComponent.#lastLineScale.set(key, to));
            this.#lineAnimations.forEach((animation) => animation.play());
        });
        this.#lineObserver.observe(this);
    }

    /** @param {Event} event */
    update = (event) => {
        const cart = /** @type {{ detail?: { resource?: CartResource } }} */ (event).detail?.resource;
        void this.#updateBlock(cart);
    };

    /** @returns {DiscountProgressBarConfig} */
    #readConfig() {
        return {
            qualifyingTags: this.#parseTags(this.dataset.qualifyingTag),
            types: this.#parseTypes(this.dataset.qualifyingTypes),
            allItems: this.hasAttribute('data-dpb-all-items'),
            minItems: Number(this.dataset.dpbMinItems) || 2,
            labels: {
                tier1: this.dataset.dpbTier1 ?? '',
                tier2: this.dataset.dpbTier2 ?? '',
                tier3: this.dataset.dpbTier3 ?? '',
            },
            messages: {
                m1: this.dataset.dpbMsg1 ?? '',
                m2: this.dataset.dpbMsg2 ?? '',
                m3: this.dataset.dpbMsg3 ?? '',
                final: this.dataset.dpbMsgFinal ?? '',
            },
            tiers: this.#parseTiers(this.dataset.dpbThresholds, this.dataset.dpbDiscounts),
            colors: {
                active: this.dataset.activeColor ?? '#DC9D70',
                inactive: this.dataset.inactiveColor ?? '#344E62',
                lineIncomplete: this.dataset.inactiveLineColor ?? '#8C9DAA',
                tagBg: this.dataset.tagBg ?? '#FFEFF1',
                tagText: this.dataset.tagText ?? '#344E62',
                message: this.dataset.messageColor ?? '#344E62',
            },
        };
    }

    /**
     * @param {string} [thresholdsRaw]
     * @param {string} [discountsRaw]
     */
    #parseTiers(thresholdsRaw, discountsRaw) {
        const thresholds = this.#parseNumbers(thresholdsRaw);
        const discounts = this.#parseNumbers(discountsRaw);
        return thresholds
            .map((thresholdCents, index) => ({
                thresholdCents,
                discountCents: discounts[index] ?? 0,
            }))
            .filter((tier) => tier.thresholdCents > 0 && tier.discountCents > 0);
    }

    /** @param {string} [raw] */
    #parseNumbers(raw) {
        if (!raw || typeof raw !== 'string') return [];
        return raw
            .split(',')
            .map((value) => Number(value.trim()))
            .filter((value) => Number.isFinite(value));
    }

    /** @param {string} [raw] */
    #parseTypes(raw) {
        if (!raw || typeof raw !== 'string') return [];
        return raw
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
    }

    /** @param {string} [raw] */
    #parseTags(raw) {
        if (!raw || typeof raw !== 'string') return [];
        return raw
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
    }

    /** @param {DiscountProgressBarConfig['colors']} colors */
    #applyThemeVars(colors) {
        this.style.setProperty('--dpb-active', colors.active);
        this.style.setProperty('--dpb-navy', colors.inactive);
        this.style.setProperty('--dpb-line-incomplete', colors.lineIncomplete);
        this.style.setProperty('--dpb-tag-bg', colors.tagBg);
        this.style.setProperty('--dpb-tag-text', colors.tagText);
        this.style.setProperty('--dpb-message', colors.message);
    }

    /** @param {CartResource} [cartResource] */
    async #updateBlock(cartResource) {
        const cfg = this.#readConfig();
        this.#applyThemeVars(cfg.colors);

        try {
            let cart = cartResource;
            if (!Array.isArray(cart?.items)) {
                const rootPath = Theme.routes.root_url ?? '/';
                const response = await fetch(`${rootPath}cart.js`);
                if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
                cart = /** @type {CartResource} */ (await response.json());
            }

            const summary = await this.#summarizeEligibleItems(cart.items ?? [], cfg);
            const progress = resolveDiscountProgress(summary.count, summary.subtotalCents, cfg.tiers, cfg.minItems);
            const segmentProgress = resolveDiscountSegmentProgress(summary.subtotalCents, cfg.tiers);

            if (summary.count === 0) {
                if (!this.hasAttribute('hidden')) this.setAttribute('hidden', '');
            } else {
                if (this.hasAttribute('hidden')) this.removeAttribute('hidden');
            }

            this.#renderMessage(progress, cfg);
            this.#maybeCelebrateTierUp(progress.unlockedTier);

            const steps = this.refs.steps ?? [];
            const completed = [progress.unlockedTier >= 1, progress.unlockedTier >= 2, progress.unlockedTier >= 3];
            steps.forEach((step) => {
                const n = parseInt(step.getAttribute('data-step') ?? '0', 10);
                if (n >= 1 && n <= 3) {
                    this.#setStepState(step, Boolean(completed[n - 1]), segmentProgress[n - 1] ?? 0, cfg.colors);
                }
            });

            this.#applyTagLabels(cfg);
        } catch (err) {
            console.error('[na-discount-progress-bar]', err);
        }
    }

    /**
     * @param {Record<string, unknown>[]} items
     * @param {DiscountProgressBarConfig} cfg
     */
    async #summarizeEligibleItems(items, cfg) {
        const byType = cfg.types.length > 0;
        const byTag = cfg.qualifyingTags.length > 0;
        if (!cfg.allItems && !byType && !byTag) return { count: 0, subtotalCents: 0 };

        const parts = await Promise.all(
            items.map(async (item) => {
                const qualifies = cfg.allItems || (await this.#lineQualifies(item, cfg));
                if (!qualifies) return { count: 0, subtotalCents: 0 };
                return {
                    count: Number(item.quantity) || 0,
                    subtotalCents: getCartLineSubtotalCents(item),
                };
            })
        );
        return parts.reduce(
            (summary, item) => ({
                count: summary.count + item.count,
                subtotalCents: summary.subtotalCents + item.subtotalCents,
            }),
            { count: 0, subtotalCents: 0 }
        );
    }

    /**
     * @param {Record<string, unknown>} item
     * @param {DiscountProgressBarConfig} cfg
     */
    async #lineQualifies(item, cfg) {
        if (cfg.types.length) {
            const pt = String(item.product_type ?? '')
                .toLowerCase()
                .trim();
            if (cfg.types.includes(pt)) return true;
        }
        if (cfg.qualifyingTags.length && item.handle) {
            return this.#qualifiesByTags(String(item.handle), cfg.qualifyingTags);
        }
        return false;
    }

    /** @param {string} handle */
    async #fetchProductTags(handle) {
        if (NaDiscountProgressBarComponent.#tagCache.has(handle)) {
            return NaDiscountProgressBarComponent.#tagCache.get(handle) ?? [];
        }
        const rootPath = Theme.routes.root_url ?? '/';
        const url = `${rootPath}products/${encodeURIComponent(handle)}.js`;
        try {
            const res = await fetch(url);
            if (!res.ok) throw new Error('product fetch');
            const product = /** @type {{ tags?: unknown[] }} */ (await res.json());
            const tags = (product.tags ?? []).map((t) => String(t).toLowerCase().trim());
            NaDiscountProgressBarComponent.#tagCache.set(handle, tags);
            return tags;
        } catch {
            NaDiscountProgressBarComponent.#tagCache.set(handle, []);
            return [];
        }
    }

    /**
     * @param {string} handle
     * @param {string[]} tagsLower
     */
    async #qualifiesByTags(handle, tagsLower) {
        const productTags = await this.#fetchProductTags(handle);
        return tagsLower.some((needle) => productTags.includes(needle));
    }

    /**
     * @param {HTMLElement} stepEl
     * @param {boolean} completed
     * @param {number} progress
     * @param {{ active: string, lineIncomplete: string }} colors
     */
    #setStepState(stepEl, completed, progress, colors) {
        const newState = completed ? 'completed' : 'locked';
        const currentState = stepEl.getAttribute('data-state');

        if (currentState !== newState) {
            stepEl.setAttribute('data-state', newState);

            const iconWrap = stepEl.querySelector('[data-dpb-icon]');
            if (iconWrap) {
                const frag = this.#cloneIconFragment(completed ? 'check' : 'lock');
                iconWrap.replaceChildren();
                if (frag) iconWrap.append(...frag.childNodes);
            }
        }

        const line = stepEl.querySelector('[data-dpb-line]');
        if (line instanceof HTMLElement) {
            const normalizedProgress = completed ? 1 : Math.min(Math.max(progress, 0), 1);
            line.style.setProperty('--dpb-line-fill', normalizedProgress > 0 ? colors.active : colors.lineIncomplete);
            line.style.setProperty('--dpb-progress', normalizedProgress.toFixed(4));
        }
    }

    /** @param {'check' | 'lock'} which */
    #cloneIconFragment(which) {
        const tmpl = /** @type {HTMLTemplateElement | null} */ (
            this.refs.iconTemplates?.querySelector(`template[data-dpb-tpl="${which}"]`)
        );
        return /** @type {DocumentFragment | null} */ (tmpl?.content?.cloneNode(true) ?? null);
    }

    /**
     * @param {ReturnType<typeof resolveDiscountProgress>} progress
     * @param {DiscountProgressBarConfig} cfg
     */
    #renderMessage(progress, cfg) {
        const replacements = {
            '[amount]': formatShopMoney(progress.amountRemainingCents, { noDecimals: true }),
            '[discount]': formatShopMoney(progress.currentDiscountCents || progress.nextDiscountCents, {
                noDecimals: true,
            }),
            '[next_discount]': formatShopMoney(progress.nextDiscountCents, { noDecimals: true }),
        };
        let raw = '';

        if (progress.stage === 'add-item') raw = cfg.messages.m1;
        if (progress.stage === 'away') raw = cfg.messages.m2;
        if (progress.stage === 'unlocked') raw = cfg.messages.m3;
        if (progress.stage === 'complete') raw = cfg.messages.final;

        for (const [token, value] of Object.entries(replacements)) {
            raw = raw.split(token).join(value);
        }

        this.refs.message.innerHTML = `<div class="na-discount-progress-bar__message-text">${this.#sanitizeRichtext(raw)}</div>`;
    }

    /** @param {string} html */
    #sanitizeRichtext(html) {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        /**
         * @param {Node} node
         * @returns {string}
         */
        const walk = (node) => {
            const out = [];
            for (const child of node.childNodes) {
                if (child.nodeType === Node.TEXT_NODE) {
                    const d = document.createElement('span');
                    d.textContent = child.textContent;
                    out.push(d.innerHTML);
                } else if (child instanceof Element) {
                    const tag = child.tagName.toLowerCase();
                    if (NaDiscountProgressBarComponent.#ALLOWED_TAGS.has(tag)) {
                        out.push(`<${tag}>${walk(child)}</${tag}>`);
                    } else {
                        out.push(walk(child));
                    }
                }
            }
            return out.join('');
        };
        return walk(doc.body);
    }

    /**
     * @param {DiscountProgressBarConfig} cfg
     */
    #applyTagLabels(cfg) {
        /** @param {Element | null} labelEl @param {string} text */
        const apply = (labelEl, text) => {
            if (!labelEl) return;
            const value = (text || '').trim();
            if (labelEl.textContent !== value) labelEl.textContent = value;
            const wrap = labelEl.closest('.na-discount-progress-bar__tag');
            if (wrap instanceof HTMLElement) wrap.hidden = value === '';
        };

        const tag1 = this.querySelector('[data-dpb-tag-label="1"]');
        const tag2 = this.querySelector('[data-dpb-tag-label="2"]');
        const tag3 = this.querySelector('[data-dpb-tag-label="3"]');
        apply(tag1, cfg.labels.tier1);
        apply(tag2, cfg.labels.tier2);
        apply(tag3, cfg.labels.tier3);
    }

    /** @param {number} tier */
    #maybeCelebrateTierUp(tier) {
        if (this.#previousTier === -1) {
            this.#previousTier = tier;
            return;
        }
        const prev = this.#previousTier;
        this.#previousTier = tier;
        if (tier > prev && tier >= 1) {
            this.classList.remove('na-discount-progress-bar--celebrate');
            void this.offsetWidth;
            this.classList.add('na-discount-progress-bar--celebrate');
            window.setTimeout(
                () => this.classList.remove('na-discount-progress-bar--celebrate'),
                NaDiscountProgressBarComponent.#CELEBRATE_MS
            );
        }
    }
}

if (!customElements.get('na-discount-progress-bar-component')) {
    customElements.define('na-discount-progress-bar-component', NaDiscountProgressBarComponent);
}
